import React from 'react';
import Image from 'frontastic/lib/image';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Tiempos, NationalBook } from 'fonts';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, textAlignmentClass, SectionCtas, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Mesa — the category listing page's intro header: a full-bleed image beside the category title and
 * rich-text copy. The CMS-driven equivalent of the hardcoded `product-list/bathroom` block (see
 * /au/tapware-and-accessories/basin-mixers/), minus that component's breadcrumb, which the page
 * shell owns rather than the section.
 *
 * Class-for-class the same markup as caroma's Mesa, so the two storefronts stay in step; the brand
 * fonts and the shared `SectionCtas` buttons are the Methven-specific part.
 */
export default function MesaRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, cards = [], content_alignment, cards_block_alignment, ctas, additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'MesaRendering');

  const image = cards[0]?.desktop_image;
  const isCardsLeft = cards_block_alignment === 'Left';

  //Unset alignment keeps the legacy look - the title centres over the stacked layout and goes left
  //once the copy sits beside the image; the copy itself stays left at every width.
  const titleAlignClass = content_alignment ? textAlignmentClass(content_alignment) : 'text-center lg:text-left';
  const copyAlignClass = content_alignment ? textAlignmentClass(content_alignment) : '';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} relative left-full w-screen -translate-x-full lg:grid lg:grid-cols-12 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div
          id="advice-section"
          className={`flex flex-col items-center lg:col-span-12 lg:px-0 ${isCardsLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
        >
          {/* No image, no box: an empty fixed-height box leaves a blank band above the copy. */}
          {image?.url && (
            <div className="relative h-[25rem] w-full md:h-[45rem] lg:h-[30rem] lg:flex-1 lg:flex-grow xl:h-[40rem] 2xl:h-[45rem]">
              <Image
                priority
                src={image.url}
                alt={image.description ?? title ?? ''}
                fill
                style={{ objectFit: 'cover' }}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            </div>
          )}
          {/* pt-24 below lg: stacked under the image, the title otherwise sits flush against it. */}
          <div className="flex-1 flex-grow pt-24 lg:pt-0 px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80">
            {title && (
              <Typography
                as="h1"
                className={`text-32 font-medium leading-10 text-primary-black md:text-40 lg:p-16 lg:leading-[3.5rem] xl:p-16 2xl:p-16 2xl:text-48 ${titleAlignClass} ${Tiempos.className}`}
              >
                {title}
              </Typography>
            )}
            {description && (
              <div
                className={`list-container pt-24 text-16 font-normal leading-6 text-[#333] md:text-18 lg:p-16 lg:leading-8 xl:p-16 2xl:p-16 2xl:text-20 [&>p]:mb-16 ${copyAlignClass} ${NationalBook.className}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
              />
            )}
            <SectionCtas ctas={ctas} className="pt-24 lg:px-16" />
          </div>
        </div>
      </div>
    </>
  );
}
